import React, { FormEvent, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { LoadingSpinner } from '@frame/ui-components/loading-spinner'
import { FallbackLetterImage } from '@frame/ui-components/unified-search/fallback-letter-image'
import { WidgetHeaderWithNavigation } from '@frame/ui-components/widget-header'
import { Body } from '@frame/ui-primitives/body'
import { Button } from '@frame/ui-primitives/button'
import { H2 } from '@frame/ui-primitives/h2'
import { Input } from '@frame/ui-primitives/input'

import { DirectTask, TrueworkJsStrategy } from '@frame/lib/api/tasks/types'
import { buildLogoDevUrl } from '@frame/lib/constants'
import { useRootContext } from '@frame/lib/context'
import { useSandboxStore } from '@frame/lib/stores/useSandboxStore'
import { useStore } from '@frame/lib/stores/useStore'
import { routes } from '@frame/routes'
import { DataAttribute } from '@frame/types'

export const SandboxCreds = {
  username: 'user_good',
  // file deepcode ignore NoHardcodedPasswords: <we need this for display purposes as this a test password>
  password: 'pass_good',
}

export function Form({ task }: { task: DirectTask }) {
  const navigate = useNavigate()
  const { actions } = useRootContext()
  const { company } = useSandboxStore(({ company }) => ({
    company,
  }))
  const maxEmployments = useStore((state) => state.session?.max_employments ?? 1)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [password, setPassword] = useState('')
  const [username, setUsername] = useState('')
  const [usernameError, setUsernameError] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [buttonText, setButtonText] = useState('Connect')

  function goBack() {
    navigate(-1)
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const hasUsernameError = username !== SandboxCreds.username
    const hasPasswordError = password !== SandboxCreds.password

    if (hasUsernameError) {
      setUsernameError('Please enter the sandbox username')
    }

    if (hasPasswordError) {
      setPasswordError('Please enter the sandbox password')
    }

    if (hasUsernameError || hasPasswordError) {
      setIsSubmitting(false)
    } else {
      setIsSubmitting(true)
      await new Promise((resolve) => setTimeout(resolve, 500))
      setButtonText('Verifying Credentials...')
      await new Promise((resolve) => setTimeout(resolve, 1000))
      setButtonText('Awaiting Response...')
      await new Promise((resolve) => setTimeout(resolve, 1000))
      setButtonText('Success!')
      await new Promise((resolve) => setTimeout(resolve, 500))
      await actions.completeDirectTask({
        directTaskId: task.id,
        completeDirectTaskBody: {
          strategy: TrueworkJsStrategy.CREDENTIALS,
          info: {
            connector_id: task.strategy_info?.connector_id,
            employer_name: task.employer_name ?? '',
            public_token: 'public_token',
            metadata: {},
          },
        },

        multiEmployer: Boolean(maxEmployments > 1),
      })
      navigate(routes.root, { replace: true, state: { fromLoginSuccess: true, taskId: task?.id } })
    }
  }

  return (
    <>
      <WidgetHeaderWithNavigation
        backButton={{ onClick: goBack }}
        cancelButton={{ onClick: () => navigate(routes.confirmExit), visible: true }}
      />
      <Body data-cy={DataAttribute.PARTNER_SCREEN_SANDBOX_FORM} className="p-5" paddingTopOverride={0}>
        <div className="mb-5 mt-0 flex flex-col items-center">
          <div className="h-8 w-8">
            {!company.domain ? (
              <FallbackLetterImage letter={company.name[0]} className="h-8 w-8" />
            ) : (
              <img alt="logo" src={buildLogoDevUrl(company.domain, 64)} className="h-8 w-8 rounded-full" />
            )}
          </div>
          <H2 className="mt-1 text-2xl font-normal">Log in to {company.name}</H2>
        </div>
        <form onSubmit={handleSubmit}>
          <Input
            data-cy={DataAttribute.PARTNER_SCREEN_SANDBOX_FORM_USERNAME}
            name="username"
            placeholder="Enter username"
            tagType="required"
            label="Username"
            value={username}
            onChange={(e) => {
              if (usernameError) {
                setUsernameError('')
              }
              setUsername(e.target.value)
            }}
            className="mb-4"
            error={usernameError}
            applyCobranding={false}
          />
          <Input
            data-cy={DataAttribute.PARTNER_SCREEN_SANDBOX_FORM_PASSWORD}
            name="password"
            placeholder="Enter password"
            tagType="required"
            label="Password"
            value={password}
            onChange={(e) => {
              if (passwordError) {
                setPasswordError('')
              }
              setPassword(e.target.value)
            }}
            type="password"
            error={passwordError}
            applyCobranding={false}
          />
          <Button
            className="mt-5"
            data-cy={DataAttribute.PARTNER_SCREEN_SANDBOX_FORM_SUBMIT}
            disabled={!password || !username || isSubmitting}
            Icon={isSubmitting ? LoadingSpinner : undefined}
            type="submit"
          >
            {buttonText}
          </Button>
        </form>
      </Body>
    </>
  )
}
